package com.matchpredictor.web

import com.matchpredictor.web.api.ApiClient
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import org.w3c.dom.EventSource

object ChatStream {
    private val scope = MainScope()
    private val json = Json { ignoreUnknownKeys = true }
    private var source: EventSource? = null
    private var reconnectTimer: Int? = null
    private var reconnectAttempt = 0
    private var generation = 0
    private var active = false
    private var onMessage: (ChatMessageDto) -> Unit = {}
    private var onConnectionChange: (Boolean) -> Unit = {}
    private var onConnected: () -> Unit = {}

    fun connect(
        onMessage: (ChatMessageDto) -> Unit,
        onConnectionChange: (Boolean) -> Unit,
        onConnected: () -> Unit
    ) {
        disconnect()
        active = true
        generation += 1
        this.onMessage = onMessage
        this.onConnectionChange = onConnectionChange
        this.onConnected = onConnected
        open(generation)
    }

    fun disconnect() {
        active = false
        generation += 1
        source?.close()
        source = null
        reconnectTimer?.let(window::clearTimeout)
        reconnectTimer = null
        reconnectAttempt = 0
    }

    private fun open(expectedGeneration: Int) {
        if (!active || expectedGeneration != generation) return
        scope.launch {
            runCatching { ApiClient.chatStreamTicket() }
                .onSuccess { response ->
                    if (!active || expectedGeneration != generation) return@onSuccess
                    val eventSource = EventSource(ApiClient.chatStreamUrl(response.ticket))
                    source = eventSource
                    eventSource.addEventListener("open", {
                        if (source !== eventSource) return@addEventListener
                        reconnectAttempt = 0
                        onConnectionChange(true)
                        onConnected()
                    })
                    eventSource.addEventListener("message", { event ->
                        val data = event.asDynamic().data as? String ?: return@addEventListener
                        runCatching { json.decodeFromString<ChatMessageDto>(data) }
                            .onSuccess(onMessage)
                    })
                    eventSource.addEventListener("error", {
                        if (source !== eventSource) return@addEventListener
                        eventSource.close()
                        source = null
                        onConnectionChange(false)
                        scheduleReconnect(expectedGeneration)
                    })
                }
                .onFailure {
                    onConnectionChange(false)
                    scheduleReconnect(expectedGeneration)
                }
        }
    }

    private fun scheduleReconnect(expectedGeneration: Int) {
        if (!active || expectedGeneration != generation || reconnectTimer != null) return
        val delayMs = (1_000 * (1 shl reconnectAttempt.coerceAtMost(5))).coerceAtMost(30_000)
        reconnectAttempt += 1
        reconnectTimer = window.setTimeout({
            reconnectTimer = null
            open(expectedGeneration)
        }, delayMs)
    }
}
